import { useState, Fragment } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import { alpha } from '@mui/material/styles'
import { Stamp } from 'components/core/Clock'
import { MOMENT_OUTCOMES } from './vocabulary'

// A span's selection ledger as a table: every moment the selector found and
// what became of it. Three places read it — a card's "from this span", where
// the card's own row is marked (`markId`) and its siblings are what the judge
// turned down beside it; a span in the Moments page's Spans view, where a cut
// row opens its card in place (`renderCard`); and a span on Stream Ingest's "What
// the router heard", where every row plays its window from the span's own
// minutes (`onPlay`), refused ones included. A card's rows play too
// (`onPlay`, with `rowPlay` saying whether each can and whether it is the one
// playing). The card's table never opens cards, so nothing nests past one
// level. `#` is the row's place in the ledger: the N of the trace's
// `moment:N` timing and of the router strip's marks. Over each quote, when
// its moment began, on the clock the page's switch names (`zone` is the
// account's).

export const outcomeOf = (key) =>
  MOMENT_OUTCOMES.find((o) => o.key === (key || 'owed')) || {
    key,
    label: key,
    color: 'default',
    help: '',
  }

// An outcome's ink, for a mark drawn outside a chip (the router strip, the
// engine transcript): the chip's palette colour, grey for `default`.
export const outcomeInk = (theme, key) => {
  const { color } = outcomeOf(key)
  return color === 'default'
    ? theme.palette.grey[500]
    : theme.palette[color]?.main || theme.palette.grey[500]
}

// The Pipeline page's "What the router heard", opened on one account at one
// span (StreamIngest/index.js reads these three parameters).
export const routerHeardPath = ({ userId, username, spanStartMs }) => {
  const params = new URLSearchParams({
    user: userId,
    span: String(spanStartMs),
  })
  if (username) params.set('name', username)
  return `/pipeline?${params.toString()}`
}

// Quote reach (the api's step 7a): the line was said outside the moment's
// window, and the window grew to hold it, whatever the row's outcome after.
// How far, off the row's own times.
const seconds = (ms) => `${Math.round(ms / 100) / 10} s`

const reachHelp = (row) => {
  const [from, to] = row.quote_said_ms
  const where =
    from >= row.end_ms
      ? `${seconds(from - row.end_ms)} after the moment`
      : to <= row.start_ms
      ? `${seconds(row.start_ms - to)} before the moment`
      : 'past the edge of its window'
  return `Said ${where}: quote reach widened the window to hold it.`
}

export const OutcomeChip = ({ outcome, count }) => {
  const o = outcomeOf(outcome)
  return (
    <Tooltip title={o.help}>
      <Chip
        size="small"
        color={o.color}
        variant={o.key === 'cut' ? 'filled' : 'outlined'}
        label={count != null ? `${o.label} ${count}` : o.label}
        sx={{ height: 20, fontSize: 11 }}
      />
    </Tooltip>
  )
}

// A row's play button: disabled with the reason when it cannot play, a pause
// while it is the one playing.
const PlayCell = ({ row, onPlay, rowPlay }) => {
  const {
    playable = true,
    playing = false,
    help = '',
  } = rowPlay ? rowPlay(row) : {}
  return (
    <TableCell sx={{ px: 0.5 }}>
      <Tooltip title={help}>
        <span>
          <IconButton
            size="small"
            disabled={!playable}
            onClick={() => onPlay(row)}
          >
            {playing ? (
              <PauseIcon fontSize="small" />
            ) : (
              <PlayArrowIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </TableCell>
  )
}

const LedgerTable = ({ rows, markId, renderCard, onPlay, rowPlay, zone }) => {
  const [open, setOpen] = useState(null)
  const columns = 7 + (renderCard ? 1 : 0) + (onPlay ? 1 : 0)
  // Wider than a phone: it scrolls sideways there rather than squeezing a
  // word to a line.
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 560 }}>
        <TableHead>
          <TableRow>
            {onPlay && <TableCell sx={{ width: 40 }} />}
            <TableCell sx={{ width: 32 }}>#</TableCell>
            <TableCell>Quote</TableCell>
            <TableCell>Hook</TableCell>
            <TableCell align="right">Strength</TableCell>
            <TableCell align="right">Bar</TableCell>
            <TableCell>Outcome</TableCell>
            <TableCell>Rung</TableCell>
            {renderCard && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, position) => {
            const mine = markId != null && row.moment_id === markId
            const opens = renderCard && row.moment_id
            const isOpen = opens && open === row.moment_id
            return (
              <Fragment key={`${row.start_ms}-${row.end_ms}`}>
                <TableRow
                  sx={
                    mine
                      ? {
                          backgroundColor: (theme) =>
                            alpha(theme.palette.success.main, 0.1),
                        }
                      : undefined
                  }
                >
                  {onPlay && (
                    <PlayCell row={row} onPlay={onPlay} rowPlay={rowPlay} />
                  )}
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {position + 1}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
                      <Stamp ms={row.start_ms} zone={zone} format="second" />
                    </Box>
                    “{row.quote}”
                    {mine && (
                      <Chip
                        size="small"
                        label="this card"
                        sx={{ ml: 1, height: 18, fontSize: 10 }}
                      />
                    )}
                    {row.quote_said_ms && (
                      <Tooltip title={reachHelp(row)}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label="reached"
                          sx={{ ml: 1, height: 18, fontSize: 10 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, color: 'text.secondary' }}>
                    {row.hook}
                  </TableCell>
                  <TableCell align="right">{row.strength}</TableCell>
                  <TableCell align="right">{row.bar || '—'}</TableCell>
                  <TableCell>
                    <OutcomeChip outcome={row.outcome} />
                  </TableCell>
                  <TableCell>{row.rung || '—'}</TableCell>
                  {renderCard && (
                    <TableCell align="right" sx={{ width: 48 }}>
                      {opens && (
                        <Tooltip title={isOpen ? 'Hide the card' : 'Its card'}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setOpen(isOpen ? null : row.moment_id)
                            }
                          >
                            <ChevronRightIcon
                              fontSize="small"
                              sx={{
                                transform: isOpen ? 'rotate(90deg)' : 'none',
                                transition: 'transform 150ms',
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
                {isOpen && (
                  <TableRow>
                    <TableCell colSpan={columns} sx={{ py: 1.5 }}>
                      {renderCard(row.moment_id)}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

export default LedgerTable
